import { useNavigate } from 'react-router-dom';
import { RotateCw, Target, Zap, ArrowRight, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function OnboardingPage() {
  const navigate = useNavigate();
  const storeId = localStorage.getItem('store_id');

  if (storeId) {
    navigate('/dashboard');
    return null;
  }

  const features = [
    {
      icon: RotateCw,
      title: 'Spin 360° Interactivo',
      description: 'Permite a tus clientes ver productos desde todos los ángulos'
    },
    {
      icon: Target,
      title: 'Hotspots Personalizables',
      description: 'Añade puntos de interés con información adicional en tus productos'
    },
    {
      icon: Zap,
      title: 'Fácil de Usar',
      description: 'Interfaz intuitiva para subir imágenes y configurar tu viewer'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-zinc-50" data-testid="onboarding-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center mb-6">
            <img src="/fusion2v-logo.png" alt="Fusion2V Logo" className="h-24 w-auto" />
          </div>
          <h1 className="text-5xl font-bold text-zinc-900 mb-4 tracking-tight">
            Spin 360° Fusion2V para Tiendanube
          </h1>
          <p className="text-xl text-zinc-600 max-w-2xl mx-auto leading-relaxed">
            Transforma la experiencia de compra con vistas 360° interactivas y hotspots informativos
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-white rounded-xl border border-zinc-200 p-8 shadow-sm hover:shadow-lg transition-shadow duration-300"
              data-testid={`feature-card-${index}`}
            >
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                <feature.icon className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-zinc-900 mb-2">{feature.title}</h3>
              <p className="text-zinc-600 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* How it works */}
        <div className="bg-white rounded-xl border border-zinc-200 p-8 md:p-12 shadow-sm mb-12">
          <h2 className="text-3xl font-bold text-zinc-900 mb-8 text-center">Cómo Funciona</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                1
              </div>
              <h4 className="font-semibold text-zinc-900 mb-2">Instala la App</h4>
              <p className="text-zinc-600 text-sm">Conecta tu tienda de Tiendanube en un solo clic</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                2
              </div>
              <h4 className="font-semibold text-zinc-900 mb-2">Configura Sirv</h4>
              <p className="text-zinc-600 text-sm">Ingresa tus credenciales de API de Sirv</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-primary text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                3
              </div>
              <h4 className="font-semibold text-zinc-900 mb-2">Sube y Configura</h4>
              <p className="text-zinc-600 text-sm">Sube imágenes 360° y añade hotspots a tus productos</p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <div className="inline-block bg-primary/5 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-zinc-900 mb-4">¿Listo para empezar?</h3>
            <p className="text-zinc-600 mb-6 max-w-md mx-auto">
              Necesitas tener una cuenta de Sirv y una tienda en Tiendanube para usar esta aplicación
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="gap-2 text-base px-8"
                data-testid="demo-dashboard-button"
                onClick={() => {
                  localStorage.setItem('store_id', 'demo_store');
                  localStorage.setItem('store_name', 'Tienda Demo');
                  navigate('/dashboard');
                }}
              >
                Ver Dashboard Demo
                <ArrowRight className="w-5 h-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="gap-2 text-base px-8"
                data-testid="learn-more-button"
                onClick={() => window.open('https://sirv.com/features/360-product-viewer/', '_blank')}
              >
                Más sobre Sirv
              </Button>
            </div>
          </div>
        </div>

        {/* Footer Note */}
        <div className="mt-16 text-center">
          <p className="text-sm text-zinc-500">
            Esta aplicación requiere una cuenta activa de{' '}
            <a
              href="https://sirv.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
            >
              Sirv.com
            </a>
            {' '}para funcionar correctamente.
          </p>
        </div>
      </div>
    </div>
  );
}